
import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { courseService } from "@/services/course.service"
import { Review, Course } from "@/types/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Trash2, Star, Check, X, Award, Plus, Loader2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

export default function ReviewsPage() {
    const [reviews, setReviews] = useState<Review[]>([])
    const [courses, setCourses] = useState<Course[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [approvingId, setApprovingId] = useState<number | null>(null)
    const [featuringId, setFeaturingId] = useState<number | null>(null)

    // Create dialog state
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
    const [isCreating, setIsCreating] = useState(false)
    const [createForm, setCreateForm] = useState({
        course_id: "none", // Changed from ""
        rating: 5,
        title: "",
        comment: "",
        is_approved: true,
        is_featured: false,
    })

    // Filters
    const [filterCourse, setFilterCourse] = useState<string>("all")
    const [filterType, setFilterType] = useState<string>("all")
    const [filterFeatured, setFilterFeatured] = useState<string>("all")

    const { toast } = useToast()
    const PAGE_SIZE = 10

    const fetchReviews = async () => {
        setIsLoading(true)
        try {
            const params: Record<string, any> = {
                limit: PAGE_SIZE,
                offset: (page - 1) * PAGE_SIZE,
            }
            if (filterCourse !== "all") {
                params.course_slug = filterCourse
            }
            if (filterType !== "all") {
                params.type = filterType
            }
            if (filterFeatured !== "all") {
                params.is_featured = filterFeatured === "true"
            }

            const response = await courseService.getReviews(params)
            setReviews(response.results)
            setTotalCount(response.count)
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось загрузить отзывы",
                variant: "destructive",
            })
        } finally {
            setIsLoading(false)
        }
    }

    const fetchCourses = async () => {
        try {
            const response = await courseService.getCourses({ limit: 100 })
            setCourses(response.results)
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        fetchCourses()
    }, [])

    useEffect(() => {
        fetchReviews()
    }, [page, filterCourse, filterType, filterFeatured])

    const handleDelete = async () => {
        if (!reviewToDelete) return

        setIsDeleting(true)
        try {
            await courseService.deleteReview(reviewToDelete.id)
            toast({
                title: "Успешно",
                description: "Отзыв успешно удален",
            })
            setReviewToDelete(null)
            fetchReviews()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось удалить отзыв",
                variant: "destructive",
            })
        } finally {
            setIsDeleting(false)
        }
    }

    const handleApprove = async (review: Review) => {
        setApprovingId(review.id)
        try {
            if (review.is_approved) {
                await courseService.unapproveReview(review.id)
                toast({ title: "Успешно", description: "Отзыв снят с публикации" })
            } else {
                await courseService.approveReview(review.id)
                toast({ title: "Успешно", description: "Отзыв одобрен" })
            }
            fetchReviews()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось обновить статус отзыва",
                variant: "destructive",
            })
        } finally {
            setApprovingId(null)
        }
    }

    const handleFeature = async (review: Review) => {
        setFeaturingId(review.id)
        try {
            if (review.is_featured) {
                await courseService.unfeatureReview(review.id)
                toast({ title: "Успешно", description: "Отзыв удален из избранных" })
            } else {
                await courseService.featureReview(review.id)
                toast({ title: "Успешно", description: "Отзыв добавлен в избранное" })
            }
            fetchReviews()
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: "Не удалось обновить статус избранного",
                variant: "destructive",
            })
        } finally {
            setFeaturingId(null)
        }
    }

    const resetCreateForm = () => {
        setCreateForm({
            course_id: "none",
            rating: 5,
            title: "",
            comment: "",
            is_approved: true,
            is_featured: false,
        })
    }

    const handleCreate = async () => {
        if (!createForm.comment.trim()) {
            toast({
                title: "Ошибка",
                description: "Требуется комментарий",
                variant: "destructive",
            })
            return
        }

        setIsCreating(true)
        try {
            // Create the review as anonymous (without authentication)
            const reviewData = {
                // Check for "none" instead of empty string
                course_id: createForm.course_id !== "none" ? parseInt(createForm.course_id) : null,
                rating: createForm.rating,
                title: createForm.title || undefined,
                comment: createForm.comment,
            }

            const newReview = await courseService.createAnonymousReview(reviewData)

            // If we need to approve or feature, update the review
            if (createForm.is_approved) {
                await courseService.approveReview(newReview.id)
            }
            if (createForm.is_featured) {
                await courseService.featureReview(newReview.id)
            }

            toast({ title: "Успешно", description: "Анонимный отзыв успешно создан" })
            setIsCreateDialogOpen(false)
            resetCreateForm()
            fetchReviews()
        } catch (error: any) {
            console.error(error)
            const errorMessage = error?.data?.non_field_errors?.[0] ||
                error?.data?.comment?.[0] ||
                error?.data?.rating?.[0] ||
                "Не удалось создать отзыв"
            toast({
                title: "Ошибка",
                description: errorMessage,
                variant: "destructive",
            })
        } finally {
            setIsCreating(false)
        }
    }

    const renderStars = (rating: number) => {
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={`h-4 w-4 ${star <= rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                            }`}
                    />
                ))}
            </div>
        )
    }

    const columns: Column<Review>[] = [
        {
            header: "Пользователь",
            cell: (review) => (
                <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                        <AvatarImage src={review.user_avatar || undefined} />
                        <AvatarFallback>
                            {review.user_name?.charAt(0) || "?"}
                        </AvatarFallback>
                    </Avatar>
                    <div>
                        <div className="font-medium text-sm">
                            {review.user_name || (
                                <span className="text-gray-400 italic">Анонимный</span>
                            )}
                        </div>
                    </div>
                </div>
            )
        },
        {
            header: "Курс",
            cell: (review) => (
                <div>
                    {review.course_title ? (
                        <Badge variant="outline" className="text-xs">
                            {review.course_title}
                        </Badge>
                    ) : (
                        <Badge variant="secondary" className="text-xs">
                            Общий
                        </Badge>
                    )}
                </div>
            )
        },
        {
            header: "Рейтинг",
            cell: (review) => renderStars(review.rating)
        },
        {
            header: "Отзыв",
            cell: (review) => (
                <div className="max-w-[300px]">
                    {review.title && (
                        <div className="font-medium text-sm">{review.title}</div>
                    )}
                    <div className="text-xs text-gray-500 line-clamp-2">
                        {review.comment}
                    </div>
                </div>
            )
        },
        {
            header: "Статус",
            cell: (review) => (
                <div className="flex flex-col gap-1">
                    <Badge
                        variant={review.is_approved ? "default" : "secondary"}
                        className="text-xs w-fit"
                    >
                        {review.is_approved ? "Одобрен" : "Ожидание"}
                    </Badge>
                    {review.is_featured && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 text-xs w-fit">
                            Избранный
                        </Badge>
                    )}
                </div>
            )
        },
        {
            header: "Дата",
            cell: (review) => (
                <div className="text-sm text-gray-600">
                    {new Date(review.created_at).toLocaleDateString()}
                </div>
            )
        },
        {
            header: "Действия",
            className: "text-right",
            cell: (review) => (
                <div className="flex justify-end gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleApprove(review)}
                        title={review.is_approved ? "Снять публикацию" : "Одобрить"}
                        className={review.is_approved ? "text-green-600" : "text-gray-400"}
                        disabled={approvingId === review.id}
                    >
                        {approvingId === review.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : review.is_approved ? (
                            <Check className="h-4 w-4" />
                        ) : (
                            <X className="h-4 w-4" />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleFeature(review)}
                        title={review.is_featured ? "Убрать из избранного" : "Добавить в избранное"}
                        className={review.is_featured ? "text-yellow-600" : "text-gray-400"}
                        disabled={featuringId === review.id}
                    >
                        {featuringId === review.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Award className="h-4 w-4" />
                        )}
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setReviewToDelete(review)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ),
        },
    ]

    return (
        <div>
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Отзывы</h1>
                    <p className="text-gray-600 mt-1">Управление отзывами и рейтингами пользователей.</p>
                </div>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Создать отзыв
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle>Список отзывов</CardTitle>
                        <div className="flex flex-wrap gap-2">
                            <Select value={filterCourse} onValueChange={setFilterCourse}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Фильтр по курсу" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Все курсы</SelectItem>
                                    {courses.map((course) => (
                                        <SelectItem key={course.id} value={course.slug}>
                                            {course.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={filterType} onValueChange={setFilterType}>
                                <SelectTrigger className="w-[140px]">
                                    <SelectValue placeholder="Фильтр по типу" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Все типы</SelectItem>
                                    <SelectItem value="course">Отзывы курса</SelectItem>
                                    <SelectItem value="general">Общие отзывы</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={filterFeatured} onValueChange={setFilterFeatured}>
                                <SelectTrigger className="w-[140px]">
                                    <SelectValue placeholder="Фильтр по избранным" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Все</SelectItem>
                                    <SelectItem value="true">Избранный</SelectItem>
                                    <SelectItem value="false">Не избранный</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={columns}
                        data={reviews}
                        isLoading={isLoading}
                        pageCount={Math.ceil(totalCount / PAGE_SIZE)}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                </CardContent>
            </Card>

            <AlertDialog open={!!reviewToDelete} onOpenChange={(open) => !open && setReviewToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Это навсегда удалит этот отзыв{reviewToDelete?.user_name ? ` от ${reviewToDelete.user_name}` : ""}. Это действие нельзя отменить.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {isDeleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Удаление...
                                </>
                            ) : (
                                "Удалить"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Create Review Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
                setIsCreateDialogOpen(open)
                if (!open) resetCreateForm()
            }}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Создать отзыв</DialogTitle>
                        <DialogDescription>
                            Добавить новый анонимный отзыв для отображения на платформе.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="course">Курс (необязательно)</Label>
                            <Select
                                value={createForm.course_id}
                                onValueChange={(value) => setCreateForm({ ...createForm, course_id: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Выберите курс" />
                                </SelectTrigger>
                                <SelectContent>
                                    {/* Value cannot be empty string */}
                                    <SelectItem value="none">Общий отзыв (без курса)</SelectItem>
                                    {courses.map((course) => (
                                        <SelectItem key={course.id} value={String(course.id)}>
                                            {course.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid gap-2">
                            <Label>Рейтинг</Label>
                            <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        onClick={() => setCreateForm({ ...createForm, rating: star })}
                                        className="p-1 hover:scale-110 transition-transform"
                                    >
                                        <Star
                                            className={`h-6 w-6 ${star <= createForm.rating
                                                ? "fill-yellow-400 text-yellow-400"
                                                : "text-gray-300 hover:text-yellow-300"
                                                }`}
                                        />
                                    </button>
                                ))}
                                <span className="ml-2 text-sm text-gray-500 self-center">
                                    {createForm.rating} / 5
                                </span>
                            </div>
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="title">Заголовок (необязательно)</Label>
                            <Input
                                id="title"
                                placeholder="например, Отличный курс!"
                                value={createForm.title}
                                onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="comment">Комментарий *</Label>
                            <Textarea
                                id="comment"
                                placeholder="Напишите подробный отзыв..."
                                value={createForm.comment}
                                onChange={(e) => setCreateForm({ ...createForm, comment: e.target.value })}
                                className="min-h-[100px]"
                            />
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="is_approved"
                                    checked={createForm.is_approved}
                                    onCheckedChange={(checked) =>
                                        setCreateForm({ ...createForm, is_approved: checked as boolean })
                                    }
                                />
                                <Label htmlFor="is_approved" className="text-sm font-normal">
                                    Одобрен (публично виден)
                                </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="is_featured"
                                    checked={createForm.is_featured}
                                    onCheckedChange={(checked) =>
                                        setCreateForm({ ...createForm, is_featured: checked as boolean })
                                    }
                                />
                                <Label htmlFor="is_featured" className="text-sm font-normal">
                                    Избранный
                                </Label>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsCreateDialogOpen(false)}
                            disabled={isCreating}
                        >
                            Отмена
                        </Button>
                        <Button onClick={handleCreate} disabled={isCreating}>
                            {isCreating ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Создание...
                                </>
                            ) : (
                                "Создать отзыв"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
